import { useEffect, useRef, useState } from 'react';
import { FOLLOWUP_STEP_DAYS } from '@epasu/core/constants';
import { crm } from '@/lib/crm-client';

/**
 * Das Häkchen für die Erstansprache, samt seiner Vorbelegung.
 *
 * Beides steht hier zusammen, weil es zusammengehört und an zwei Stellen
 * gebraucht wird — im ComposeDialog und im MailComposer. Dasselbe Muster wie
 * bei `AnhangWahl`, das ebenfalls neben dem Dialog wohnt, der es zuerst
 * brauchte.
 */

export interface Erstansprache {
  /** Zustand des Häkchens — genau das geht als `outreach` mit raus. */
  aktiv: boolean;
  setzen: (wert: boolean) => void;
  /** Wie viele der gewählten Empfänger noch nie Post von uns hatten. */
  erstkontakte: number;
  empfaenger: number;
}

/**
 * Fragt für die gewählten Empfänger nach, wer davon ein Erstkontakt ist, und
 * belegt das Häkchen damit vor: gesetzt, sobald mindestens einer neu ist.
 *
 * Sobald jemand das Häkchen von Hand angefasst hat, bleibt es, wie es gesetzt
 * wurde — auch wenn danach noch Empfänger dazukommen. Die menschliche
 * Entscheidung sticht die Erkennung, und eine Vorbelegung, die eine bewusste
 * Änderung hinterrücks zurückdreht, wäre genau der Fehler, vor dem der
 * Entwurf warnt.
 *
 * Scheitert die Abfrage, bleibt das Häkchen, wie es ist. Eine Fehlermeldung
 * über einer Vorbelegung wäre lauter als der Sachverhalt: schlimmstenfalls
 * startet die Uhr nicht, und das lässt sich am Verlauf nachträglich drehen.
 */
export function useErstansprache(contactIds: string[]): Erstansprache {
  const [aktiv, setAktiv] = useState(false);
  const [erstkontakte, setErstkontakte] = useState(0);
  const vonHand = useRef(false);

  // Die Empfängerliste ist bei jedem Rendern ein neues Array. Als Abhängigkeit
  // taugt deshalb nur ihr Inhalt, sonst liefe die Abfrage endlos.
  const schluessel = contactIds.join(',');
  const ids = schluessel ? schluessel.split(',') : [];

  useEffect(() => {
    const gewaehlt = schluessel ? schluessel.split(',') : [];
    if (gewaehlt.length === 0) {
      setErstkontakte(0);
      if (!vonHand.current) setAktiv(false);
      return;
    }

    let abgebrochen = false;
    void crm.mail.outreachCheck(gewaehlt).then(
      (ergebnis) => {
        if (abgebrochen) return;
        const neue = ergebnis.filter((e) => e.firstContact).length;
        setErstkontakte(neue);
        if (!vonHand.current) setAktiv(neue > 0);
      },
      () => {
        /* siehe Doc-Kommentar: still bleiben */
      },
    );

    return () => {
      abgebrochen = true;
    };
  }, [schluessel]);

  return {
    aktiv,
    erstkontakte,
    empfaenger: ids.length,
    setzen: (wert: boolean) => {
      vonHand.current = true;
      setAktiv(wert);
    },
  };
}

/** In wie vielen Tagen nach dem Versand das erste Nachfassen ansteht. */
const ERSTE_FRIST = FOLLOWUP_STEP_DAYS[0] ?? 3;

/**
 * Das Häkchen selbst, mit der Zahl der erkannten Erstkontakte darunter.
 *
 * Die Zahl steht auch dann da, wenn sie 0 ist: wer das Häkchen trotzdem setzt,
 * soll sehen, dass er der Erkennung widerspricht.
 */
export function ErstanspracheHaken({ zustand }: { zustand: Erstansprache }) {
  const { aktiv, setzen, erstkontakte, empfaenger } = zustand;

  return (
    <div className="space-y-1">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={aktiv}
          onChange={(e) => setzen(e.target.checked)}
        />
        <span>Erstansprache — in {ERSTE_FRIST} Tagen ans Nachfassen erinnern</span>
      </label>

      {empfaenger > 0 && (
        <p className="pl-6 text-xs text-muted-foreground">{hinweis(erstkontakte, empfaenger)}</p>
      )}
    </div>
  );
}

function hinweis(erstkontakte: number, empfaenger: number): string {
  if (empfaenger === 1) {
    return erstkontakte === 1
      ? 'Dieser Kontakt ist ein Erstkontakt — es gibt noch keine Nachricht mit ihm.'
      : 'Mit diesem Kontakt gab es schon Schriftverkehr.';
  }
  return `${erstkontakte} von ${empfaenger} Empfängern sind Erstkontakte`;
}
